import { useState } from "react";
import { TeamMember } from "@/api/teamMembers";
import { Mail, Phone, UserCircle } from "lucide-react";
import { formatPhone } from "@/utils/formatPhone";

interface TeamMemberHighlightsProps {
  teamMember: TeamMember;
  primaryColor?: string;
}

const TeamMemberHighlights: React.FC<TeamMemberHighlightsProps> = ({
  teamMember,
  primaryColor = "#007bff",
}) => {
  const [expandedBio, setExpandedBio] = useState(false);

  const fullBioText = teamMember.bio || "";

  // Set a character limit for the short bio text (approximately 4-5 sentences)
  const shortBioTextLimit = Math.min(400, fullBioText.length);
  const shortBioText =
    fullBioText.length > shortBioTextLimit
      ? fullBioText.substring(0, shortBioTextLimit) + "..."
      : fullBioText;

  const toggleBioExpansion = () => {
    setExpandedBio(!expandedBio);
  };

  return (
    <>
      <div className="flex flex-col md:flex-row items-start justify-between md:items-center mb-2 gap-3">
        <h2 className="text-lg md:text-2xl font-bold">
          Meet Your Dedicated Agent
        </h2>
      </div>
      <p className="text-sm xl:text-lg 3xl:text-xl mb-5 xl:mb-8">
        Have questions? Need assistance? Contact your agent for any real estate
        inquiries.
      </p>
      <div className="overflow-hidden rounded-2xl transition-shadow duration-300">
        <div className="p-0">
          {/* Agent info section */}
          <div className="flex flex-col md:flex-row gap-7 md:items-center">
            {/* Agent photo */}
            <div className="md:w-1/3 flex-shrink-0 bg-white shadow-md border border-gray-100 rounded-2xl p-4 xl:p-8">
              <div className="w-full aspect-[3/4] bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg flex items-center justify-center shadow-md relative border-2 border-white">
                {teamMember.photo_url ? (
                  <img
                    src={teamMember.photo_url}
                    alt={`${teamMember.first_name} ${teamMember.last_name}`}
                    className="w-full object-cover object-top rounded-lg shadow-md"
                  />
                ) : (
                  <div className="relative">
                    <UserCircle className="w-24 h-24 text-gray-400" />
                  </div>
                )}
              </div>
            </div>

            {/* Agent details */}
            <div className="md:w-2/3 bg-white shadow-md border border-gray-100 rounded-2xl flex flex-col justify-center  p-4 xl:p-8">
              <h3
                className="text-2xl md:text-3xl font-bold xl:mb-4"
                style={{ color: primaryColor }}
              >
                {teamMember.first_name} {teamMember.last_name}
              </h3>

              {/* Contact info */}
              <div className="mb-4 space-y-2">
                {teamMember.email && (
                  <a
                    href={`mailto:${teamMember.email}`}
                    className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
                  >
                    <Mail className="w-4 h-4" />
                    <span className="text-sm xl:text-base underline">
                      {teamMember.email}
                    </span>
                  </a>
                )}
                {teamMember.phone_number && (
                  <a
                    href={`tel:${teamMember.phone_number}`}
                    className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
                  >
                    <Phone className="w-4 h-4" />
                    <span className="text-sm xl:text-base  underline">
                      {formatPhone(teamMember.phone_number)}
                    </span>
                  </a>
                )}
              </div>

              {/* Bio section */}
              <div className="text-gray-700 mb-8  rounded-lg">
                <p className="text-sm xl:text-base">
                  {expandedBio ? fullBioText : shortBioText}
                  {fullBioText.length > shortBioTextLimit && (
                    <button
                      onClick={toggleBioExpansion}
                      className="ml-1 text-primary-600 hover:text-primary-800 bg-transparent p-0 underline font-semibold text-sm"
                    >
                      {expandedBio ? "Read Less" : "Read More"}
                    </button>
                  )}
                </p>
              </div>

              {/* Contact buttons */}
              <div className="flex flex-col gap-3">
                <div className="flex flex-row gap-3">
                  <a
                    href={`mailto:${teamMember.email}?subject=Real Estate Inquiry`}
                    className="flex-1 flex items-center justify-center text-white py-3 text-sm font-medium rounded-lg hover:opacity-90 transition-all shadow-sm text-center"
                    style={{
                      background: `linear-gradient(to right, ${primaryColor}, ${primaryColor}CC)`,
                    }}
                  >
                    <Mail className="w-5 h-5 mr-2" />
                    Email Agent
                  </a>
                  <a
                    href={`tel:${teamMember.phone_number}`}
                    className="flex-1 flex items-center justify-center bg-white py-3 text-sm font-medium rounded-lg hover:bg-gray-50 transition-all shadow-sm border text-center"
                    style={{ color: primaryColor, borderColor: primaryColor }}
                  >
                    <Phone className="w-5 h-5 mr-2" />
                    Call Agent
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default TeamMemberHighlights;
